import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import { SUPPORTED_LANGUAGES } from '../../i18n/languages';
import { applyI18nKeyTranslations } from '../../utils/applyI18nResources';
import './i18n-manager.css';

const PAGE_SIZE = 50;
const COLS_STORAGE_KEY = 'i18n_mgr_visible_cols';
const PRIMARY_LANG_KEY = 'i18n_mgr_primary_lang';

const DEFAULT_VISIBLE_COLS = ['ru', 'en', 'hy', 'ka', 'tr', 'de', 'fr', 'es'];

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

function loadVisibleCols() {
    try {
        const raw = localStorage.getItem(COLS_STORAGE_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE_COLS;
}

function langMeta(code) {
    return SUPPORTED_LANGUAGES.find((l) => l.code === code) || { code, shortCode: code.toUpperCase(), flag: '🌐', nativeLabel: code };
}

function StatCard({ icon, label, value, delta, t }) {
    const deltaCls = delta > 0 ? 'i18n-mgr__stat-delta--up' : delta < 0 ? 'i18n-mgr__stat-delta--down' : '';
    return (
        <div className="i18n-mgr__stat">
            <div className="i18n-mgr__stat-icon">{icon}</div>
            <div className="i18n-mgr__stat-label">{label}</div>
            <div className="i18n-mgr__stat-value">{value}</div>
            {delta != null && delta !== 0 && (
                <div className={`i18n-mgr__stat-delta ${deltaCls}`}>
                    {delta > 0 ? '▲' : '▼'} {Math.abs(delta)} {t('stats.sinceLastAction')}
                </div>
            )}
        </div>
    );
}

function Pagination({ page, total, pageSize, onChange, t }) {
    const pages = Math.max(1, Math.ceil(total / pageSize));
    return (
        <div className="i18n-mgr__pager">
            <span style={{ fontSize: 13, color: 'var(--sp-gray-600)' }}>
                {t('pagination.showing', {
                    from: total === 0 ? 0 : (page - 1) * pageSize + 1,
                    to: Math.min(page * pageSize, total),
                    total,
                })}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button type="button" className="sp-btn sp-btn--outline sp-btn--sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
                    ← {t('pagination.prev')}
                </button>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{page} / {pages}</span>
                <button type="button" className="sp-btn sp-btn--outline sp-btn--sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
                    {t('pagination.next')} →
                </button>
            </div>
        </div>
    );
}

function LanguageColumnBar({ visibleCols, primaryLang, onToggle, onPrimary, onShowAll, onHideExtra, t }) {
    return (
        <div className="i18n-mgr__lang-bar">
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-gray-600)' }}>{t('langBar.columns')}:</span>
            {SUPPORTED_LANGUAGES.map((lang) => {
                const on = visibleCols.includes(lang.code);
                const isPrimary = primaryLang === lang.code;
                return (
                    <button
                        key={lang.code}
                        type="button"
                        className={`i18n-mgr__lang-chip${on ? ' i18n-mgr__lang-chip--on' : ''}${isPrimary ? ' i18n-mgr__lang-chip--primary' : ''}`}
                        onClick={() => onToggle(lang.code)}
                        onDoubleClick={() => onPrimary(lang.code)}
                        title={t('langBar.chipHint')}
                    >
                        {lang.flag} {lang.shortCode || lang.code.toUpperCase()}
                        {isPrimary ? ' ★' : ''}
                    </button>
                );
            })}
            <button type="button" className="sp-btn sp-btn--outline sp-btn--sm" onClick={onShowAll}>{t('langBar.showAll')}</button>
            <button type="button" className="sp-btn sp-btn--outline sp-btn--sm" onClick={onHideExtra}>{t('langBar.compact')}</button>
        </div>
    );
}

function EditKeyModal({ keyData, onSave, onClose, t, ensureDeepSeekKey }) {
    const [translations, setTranslations] = useState({ ...keyData.translations });
    const [isManual, setIsManual] = useState(keyData.isManual);
    const [manualNote, setManualNote] = useState(keyData.manualNote || '');
    const [saving, setSaving] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [activeLang, setActiveLang] = useState('ru');

    async function translateKey(overwrite = false) {
        const ready = await ensureDeepSeekKey();
        if (!ready) {
            alert(t('demo.needsApiKey'));
            return;
        }
        setTranslating(true);
        try {
            const { data } = await adminApi().post('/admin/i18n/translate-key', {
                key: keyData.key,
                overwrite,
            });
            const merged = { ...translations, ...data.translations };
            setTranslations(merged);
            applyI18nKeyTranslations(keyData.key, data.translations);
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setTranslating(false);
        }
    }

    async function handleSave() {
        setSaving(true);
        try {
            const batchTranslations = {};
            for (const lang of SUPPORTED_LANGUAGES) {
                const value = translations[lang.code];
                if (value == null || String(value).trim() === '') continue;
                batchTranslations[lang.code] = String(value).trim();
            }

            await adminApi().post('/admin/i18n/key/batch', {
                key: keyData.key,
                translations: batchTranslations,
                isManual,
                manualNote: isManual ? manualNote : null,
            });

            applyI18nKeyTranslations(keyData.key, batchTranslations);
            onSave(keyData.key, { ...translations, ...batchTranslations });
        } catch (err) {
            console.error('Save failed:', err);
            alert(err.response?.data?.message || err.message || t('saveError'));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
            <div className="sp-data-card" style={{ maxWidth: 720, width: '95%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
                <h2 style={{ margin: '0 0 8px' }}>{t('editModal.title')}</h2>
                <code style={{ fontSize: 13, color: 'var(--sp-gray-600)' }}>{keyData.key}</code>

                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className="sp-btn sp-btn--outline sp-btn--sm"
                        disabled={translating || saving}
                        onClick={() => translateKey(false)}
                    >
                        🤖 {t('demo.translateRowMissing')}
                    </button>
                    <button
                        type="button"
                        className="sp-btn sp-btn--outline sp-btn--sm"
                        disabled={translating || saving}
                        onClick={() => {
                            if (window.confirm(t('demo.translateRowAllHint'))) {
                                translateKey(true);
                            }
                        }}
                    >
                        🤖 {t('demo.translateRowAll')}
                    </button>
                </div>

                <div className="i18n-mgr__lang-bar" style={{ marginTop: 16 }}>
                    {SUPPORTED_LANGUAGES.map((lang) => (
                        <button
                            key={lang.code}
                            type="button"
                            className={`i18n-mgr__lang-chip${activeLang === lang.code ? ' i18n-mgr__lang-chip--on' : ''}`}
                            onClick={() => setActiveLang(lang.code)}
                        >
                            {lang.flag} {lang.nativeLabel}
                            {!translations[lang.code] ? ' ❌' : ''}
                        </button>
                    ))}
                </div>

                {SUPPORTED_LANGUAGES.filter((l) => l.code === activeLang).map((lang) => (
                    <div key={lang.code} style={{ marginBottom: 16 }}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                            {lang.flag} {lang.nativeLabel} ({lang.code})
                        </label>
                        <textarea
                            className="sp-input"
                            value={translations[lang.code] || ''}
                            onChange={(e) => setTranslations({ ...translations, [lang.code]: e.target.value })}
                            rows={4}
                            style={{ width: '100%', background: !translations[lang.code] ? '#fff8f8' : undefined }}
                        />
                    </div>
                ))}

                <div className="i18n-mgr__panel" style={{ marginTop: 8, padding: 14 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}>
                        <input type="checkbox" checked={isManual} onChange={(e) => setIsManual(e.target.checked)} />
                        {t('editModal.protect')}
                    </label>
                    {isManual && (
                        <>
                            <input
                                className="sp-input"
                                placeholder={t('editModal.protectNote')}
                                value={manualNote}
                                onChange={(e) => setManualNote(e.target.value)}
                                style={{ width: '100%', marginTop: 10 }}
                            />
                            <p style={{ color: '#c62828', fontSize: 13, margin: '8px 0 0' }}>{t('editModal.protectWarning')}</p>
                        </>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                    <button type="button" className="sp-btn sp-btn--outline" onClick={onClose}>{t('actions.cancel')}</button>
                    <button type="button" className="sp-btn sp-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving ? t('jobRunning') : t('actions.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function I18nManagerPage() {
    const { t } = useTranslation('adminI18n');
    const [tab, setTab] = useState('dashboard');
    const [missingSub, setMissingSub] = useState('partial');
    const [report, setReport] = useState(null);
    const [namespaces, setNamespaces] = useState([]);
    const [keys, setKeys] = useState([]);
    const [keysTotal, setKeysTotal] = useState(0);
    const [history, setHistory] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState('');
    const [editKey, setEditKey] = useState(null);
    const [actionLog, setActionLog] = useState([]);
    const [lastDelta, setLastDelta] = useState(null);
    const [visibleCols, setVisibleCols] = useState(loadVisibleCols);
    const [primaryLang, setPrimaryLang] = useState(() => localStorage.getItem(PRIMARY_LANG_KEY) || 'ru');
    const [filters, setFilters] = useState({
        search: '',
        lang: 'all',
        status: 'all',
        namespace: 'all',
        page: 1,
    });
    const [historyPage, setHistoryPage] = useState(1);
    const [missingPage, setMissingPage] = useState(1);
    const [missingItems, setMissingItems] = useState([]);
    const [missingTotal, setMissingTotal] = useState(0);
    const [i18nSettings, setI18nSettings] = useState(null);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [deepSeekInput, setDeepSeekInput] = useState('');
    const [settingsMsg, setSettingsMsg] = useState('');
    const [demoSchools, setDemoSchools] = useState([]);
    const [demoSchoolId, setDemoSchoolId] = useState('');
    const [demoEntityType, setDemoEntityType] = useState('groups');
    const [demoRows, setDemoRows] = useState([]);
    const [demoTotal, setDemoTotal] = useState(0);
    const [demoPage, setDemoPage] = useState(1);
    const [demoSearch, setDemoSearch] = useState('');
    const [demoBusy, setDemoBusy] = useState('');
    const [editDemoRow, setEditDemoRow] = useState(null);
    const [translatingKey, setTranslatingKey] = useState(null);
    const [translatingNamespace, setTranslatingNamespace] = useState(null);

    const displayCols = useMemo(() => {
        const cols = visibleCols.filter((c) => SUPPORTED_LANGUAGES.some((l) => l.code === c));
        if (!cols.includes('ru')) return ['ru', ...cols];
        return cols;
    }, [visibleCols]);

    const loadReport = useCallback(async () => {
        const { data } = await adminApi().get('/admin/i18n/report');
        setReport(data);
    }, []);

    const loadNamespaces = useCallback(async () => {
        const { data } = await adminApi().get('/admin/i18n/namespaces');
        setNamespaces(data.items || []);
    }, []);

    const loadSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const { data } = await adminApi().get('/admin/i18n/settings');
            setI18nSettings(data);
            return data;
        } catch (err) {
            console.warn('Failed to load i18n settings:', err);
            return null;
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    /** Always fetch fresh key status from API — avoids stale/null i18nSettings after failed loads. */
    const ensureDeepSeekKey = useCallback(async () => {
        try {
            const { data } = await adminApi().get('/admin/i18n/settings');
            setI18nSettings(data);
            if (data?.keyDecryptFailed) {
                alert(t('settings.keyDecryptFailed'));
                return false;
            }
            return data?.hasDeepSeekApiKey === true;
        } catch (err) {
            console.warn('Failed to verify DeepSeek API key:', err);
            return false;
        }
    }, [t]);

    const loadDemoSchools = useCallback(async () => {
        const { data } = await adminApi().get('/admin/demo-translations/schools');
        const items = data.items || [];
        setDemoSchools(items);
        setDemoSchoolId((prev) => prev || (items[0] ? String(items[0].id) : ''));
    }, []);

    const loadDemoRows = useCallback(async (override = {}) => {
        const schoolId = override.schoolId ?? demoSchoolId;
        if (!schoolId) return;
        const page = override.page ?? demoPage;
        const search = override.search ?? demoSearch;
        const entityType = override.entityType ?? demoEntityType;
        const { data } = await adminApi().get('/admin/demo-translations/rows', {
            params: { schoolId, entityType, search: search || undefined, page, pageSize: PAGE_SIZE },
        });
        setDemoRows(data.items || []);
        setDemoTotal(data.total || 0);
    }, [demoSchoolId, demoPage, demoSearch, demoEntityType]);

    const loadKeys = useCallback(async (override = {}) => {
        const f = { ...filters, ...override };
        const { data } = await adminApi().get('/admin/i18n/keys', {
            params: {
                lang: f.lang === 'all' ? undefined : f.lang,
                status: f.status === 'all' ? undefined : f.status,
                search: f.search || undefined,
                ns: f.namespace === 'all' ? undefined : f.namespace,
                page: f.page,
                pageSize: PAGE_SIZE,
            },
        });
        setKeys(data.items || []);
        setKeysTotal(data.total || 0);
    }, [filters]);

    const loadHistory = useCallback(async (page = historyPage) => {
        const { data } = await adminApi().get('/admin/i18n/history', { params: { page, pageSize: PAGE_SIZE } });
        setHistory(data.items || []);
        setHistoryTotal(data.total || 0);
    }, [historyPage]);

    const loadMissingPartial = useCallback(async (page = missingPage) => {
        const { data } = await adminApi().get('/admin/i18n/keys', {
            params: { status: 'partial', page, pageSize: PAGE_SIZE, search: filters.search || undefined, ns: filters.namespace === 'all' ? undefined : filters.namespace },
        });
        setMissingItems(data.items || []);
        setMissingTotal(data.total || 0);
    }, [missingPage, filters.search, filters.namespace]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([loadReport(), loadNamespaces()]);
            if (tab === 'keys') await loadKeys();
            if (tab === 'history') await loadHistory();
            if (tab === 'missing' && missingSub === 'partial') await loadMissingPartial();
        } catch {
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [loadReport, loadNamespaces, loadKeys, loadHistory, loadMissingPartial, tab, missingSub]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (tab === 'keys') loadKeys();
        if (tab === 'history') loadHistory(historyPage);
        if (tab === 'missing' && missingSub === 'partial') loadMissingPartial(missingPage);
    }, [tab, historyPage, missingPage, missingSub, loadKeys, loadHistory, loadMissingPartial]);

    useEffect(() => {
        const tmr = setTimeout(() => {
            if (tab === 'keys') loadKeys({ page: 1 });
            if (tab === 'missing' && missingSub === 'partial') {
                setMissingPage(1);
                loadMissingPartial(1);
            }
        }, 400);
        return () => clearTimeout(tmr);
    }, [filters.search, filters.namespace, filters.status, filters.lang, tab, missingSub, loadKeys, loadMissingPartial]);

    function pushLog(entry) {
        setActionLog((prev) => [entry, ...prev].slice(0, 20));
    }

    const [auditResult, setAuditResult] = useState(null);

    function applyActionResult(data, label) {
        const delta = (data.after?.fullyTranslated ?? 0) - (data.before?.fullyTranslated ?? 0);
        setLastDelta({ fullyTranslated: delta, partial: (data.after?.partial ?? 0) - (data.before?.partial ?? 0) });
        if (data.missingCount != null) {
            setAuditResult({
                missingCount: data.missingCount,
                autoAdded: data.autoAdded ?? 0,
            });
        }
        pushLog({
            time: new Date().toLocaleString(),
            label,
            message: data.message,
            output: data.output,
        });
    }

    async function runAction(action, langs = null) {
        if (action === 'rebuild' && !window.confirm(t('confirm.rebuild'))) return;
        if (action === 'translate' && !langs && !window.confirm(t('confirm.translateAll'))) return;
        if (action === 'translate') {
            const ready = await ensureDeepSeekKey();
            if (!ready) {
                alert(t('demo.needsApiKey'));
                return;
            }
        }

        setBusy(action);
        try {
            if (action === 'audit') {
                const { data } = await adminApi().post('/admin/i18n/audit');
                applyActionResult(data, t('actions.audit'));
            }
            if (action === 'rebuild') {
                const { data } = await adminApi().post('/admin/i18n/rebuild');
                applyActionResult(data, t('actions.rebuild'));
            }
            if (action === 'translate') {
                const { data } = await adminApi().post('/admin/i18n/translate-missing', { languages: langs });
                pushLog({ time: new Date().toLocaleString(), label: t('actions.translateAll'), message: t('jobRunning') });
                let status = 'running';
                while (status === 'running') {
                    await new Promise((r) => setTimeout(r, 2000));
                    const st = await adminApi().get(`/admin/i18n/translate-status/${data.jobId}`);
                    status = st.data.status;
                    if (status === 'failed') {
                        pushLog({ time: new Date().toLocaleString(), label: t('actions.translateAll'), message: st.data.message || 'failed' });
                        break;
                    }
                    if (status === 'completed') {
                        pushLog({ time: new Date().toLocaleString(), label: t('actions.translateAll'), message: st.data.message || t('translated') });
                    }
                }
            }
            await refresh();
        } catch (err) {
            pushLog({
                time: new Date().toLocaleString(),
                label: action,
                message: err.response?.data?.message || err.message || t('loadError'),
            });
        } finally {
            setBusy('');
        }
    }

    async function openEdit(keyRow) {
        const parts = keyRow.key.split('.');
        const ns = parts[0];
        const rest = parts.slice(1).join('.');
        const { data } = await adminApi().get(`/admin/i18n/key/${ns}/${rest}`);
        setEditKey({
            key: data.key,
            isManual: data.isManual,
            manualNote: data.manualNote,
            translations: Object.fromEntries((data.translations || []).map((tr) => [tr.lang, tr.value])),
        });
    }

    function toggleCol(code) {
        setVisibleCols((prev) => {
            const next = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(next));
            return next;
        });
    }

    function setPrimary(code) {
        setPrimaryLang(code);
        localStorage.setItem(PRIMARY_LANG_KEY, code);
        if (!visibleCols.includes(code)) toggleCol(code);
    }

    const tabs = [
        { id: 'dashboard', label: t('tabs.dashboard') },
        { id: 'keys', label: t('tabs.keys') },
        { id: 'demo', label: t('tabs.demo') },
        { id: 'settings', label: t('tabs.settings') },
        { id: 'missing', label: t('tabs.missing') },
        { id: 'history', label: t('tabs.history') },
    ];

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        if (tab === 'settings' || tab === 'demo' || tab === 'keys') {
            loadSettings();
        }
    }, [tab, loadSettings]);

    useEffect(() => {
        if (tab === 'demo') {
            loadDemoSchools().catch(() => {});
        }
    }, [tab, loadDemoSchools]);

    useEffect(() => {
        if (tab === 'demo' && demoSchoolId) {
            loadDemoRows().catch(() => {});
        }
    }, [tab, demoSchoolId, demoEntityType, demoPage, demoSearch, loadDemoRows]);

    async function handleTranslateAll(row) {
        const ready = await ensureDeepSeekKey();
        if (!ready) {
            alert(t('demo.needsApiKey'));
            return;
        }
        setTranslatingKey(row.key);
        try {
            const { data } = await adminApi().post('/admin/i18n/translate-key', {
                key: row.key,
                overwrite: true,
            });
            const merged = { ...(row.translations || {}), ...(data.translations || {}) };
            applyI18nKeyTranslations(row.key, data.translations);
            const patchRow = (r) => (r.key === row.key
                ? {
                    ...r,
                    translations: merged,
                    missingIn: SUPPORTED_LANGUAGES
                        .map((l) => l.code)
                        .filter((code) => !merged[code]),
                }
                : r);
            setKeys((prev) => prev.map(patchRow));
            setMissingItems((prev) => prev.map(patchRow));
            pushLog({
                time: new Date().toLocaleString(),
                label: t('actions.translateAllRow'),
                message: t('translateAllDone', { count: data.translated ?? Object.keys(data.translations || {}).length }),
            });
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setTranslatingKey(null);
        }
    }

    function patchKeyRow(savedKey, savedTranslations) {
        const merged = { ...savedTranslations };
        const missingIn = SUPPORTED_LANGUAGES.map((l) => l.code).filter((code) => !merged[code]);
        return (r) => (r.key === savedKey
            ? {
                ...r,
                translations: { ...(r.translations || {}), ...merged },
                missingIn,
                isManual: r.isManual,
            }
            : r);
    }

    function handleKeySaved(savedKey, savedTranslations) {
        const patch = patchKeyRow(savedKey, savedTranslations);
        setKeys((prev) => prev.map(patch));
        setMissingItems((prev) => prev.map(patch));
        setEditKey(null);
        refresh();
    }

    async function handleTranslateNamespace(namespace) {
        if (!namespace || namespace === 'all') return;
        const ready = await ensureDeepSeekKey();
        if (!ready) {
            alert(t('demo.needsApiKey'));
            return;
        }
        if (!window.confirm(t('confirm.translateNamespace', { ns: namespace }))) return;

        setTranslatingNamespace(namespace);
        try {
            const { data } = await adminApi().post('/admin/i18n/translate-namespace', {
                namespace,
                overwrite: false,
            });
            Object.entries(data.translations || {}).forEach(([fullKey, langMap]) => {
                applyI18nKeyTranslations(fullKey, langMap);
            });
            await loadKeys();
            await loadReport();
            pushLog({
                time: new Date().toLocaleString(),
                label: t('actions.translateNamespace'),
                message: t('namespaceTranslateDone', {
                    keys: data.translatedKeys ?? 0,
                    strings: data.translatedStrings ?? 0,
                }),
            });
        } catch (err) {
            console.error(err);
            alert(err.response?.data?.message || err.message || t('namespaceTranslateError'));
        } finally {
            setTranslatingNamespace(null);
        }
    }

    async function saveDeepSeekKey() {
        if (!deepSeekInput.trim()) return;
        setSettingsMsg('');
        await adminApi().put('/admin/i18n/settings', { deepSeekApiKey: deepSeekInput.trim() });
        setDeepSeekInput('');
        await loadSettings();
        setSettingsMsg(t('settings.saved'));
    }

    async function translateDemoMissing() {
        if (!demoSchoolId) return;
        const ready = await ensureDeepSeekKey();
        if (!ready) {
            alert(t('demo.needsApiKey'));
            return;
        }
        setDemoBusy('translate');
        try {
            const { data } = await adminApi().post('/admin/demo-translations/translate-missing', {
                schoolId: Number(demoSchoolId),
                entityType: demoEntityType,
            });
            pushLog({
                time: new Date().toLocaleString(),
                label: t('demo.translateMissing'),
                message: t('demo.translateDone', { count: data.translated }),
            });
            await loadDemoRows();
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setDemoBusy('');
        }
    }

    async function translateDemoRow(row, overwrite = false) {
        const ready = await ensureDeepSeekKey();
        if (!ready) {
            alert(t('demo.needsApiKey'));
            return;
        }
        setDemoBusy(`row-${row.rowKey}`);
        try {
            const { data } = await adminApi().post('/admin/demo-translations/translate-row', {
                entityType: row.entityType,
                entityId: row.entityId,
                field: row.field,
                overwrite,
                mode: row.translationMode || row.editTranslationMode,
            });
            pushLog({
                time: new Date().toLocaleString(),
                label: overwrite ? t('demo.translateRowAll') : t('demo.translateRowMissing'),
                message: t('demo.translateDone', { count: data.translated }),
            });
            await loadDemoRows();
            if (editDemoRow?.rowKey === row.rowKey) {
                const { data: list } = await adminApi().get('/admin/demo-translations/rows', {
                    params: {
                        schoolId: demoSchoolId,
                        entityType: demoEntityType,
                        page: demoPage,
                        pageSize: PAGE_SIZE,
                        search: demoSearch || undefined,
                    },
                });
                const updated = (list.items || []).find((r) => r.rowKey === row.rowKey);
                if (updated) {
                    setEditDemoRow({
                        ...updated,
                        editTranslations: { ...updated.translations, ru: updated.ruValue },
                    });
                }
            }
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        } finally {
            setDemoBusy('');
        }
    }

    async function saveDemoRowMode(rowKey, mode) {
        await adminApi().put('/admin/demo-translations/row-mode', { rowKey, mode });
    }

    async function saveDemoRow(row, translations) {
        for (const lang of SUPPORTED_LANGUAGES) {
            const code = lang.code;
            if (code === 'ru') continue;
            const value = translations[code];
            if (value == null || value === '') continue;
            await adminApi().put('/admin/demo-translations/row', {
                entityType: row.entityType,
                entityId: row.entityId,
                field: row.field,
                lang: code,
                value,
            });
        }
        setEditDemoRow(null);
        await loadDemoRows();
    }

    const codeMissing = report?.missingInRu || [];
    const codeMissingPage = codeMissing.slice((missingPage - 1) * PAGE_SIZE, missingPage * PAGE_SIZE);
    const codeMissingTotal = codeMissing.length;

    function renderKeysTable(rows, total, page, onPageChange, showNsFilter = true) {
        return (
            <>
                {showNsFilter && (
                    <LanguageColumnBar
                        visibleCols={visibleCols}
                        primaryLang={primaryLang}
                        onToggle={toggleCol}
                        onPrimary={setPrimary}
                        onShowAll={() => {
                            const all = SUPPORTED_LANGUAGES.map((l) => l.code);
                            setVisibleCols(all);
                            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(all));
                        }}
                        onHideExtra={() => {
                            setVisibleCols(DEFAULT_VISIBLE_COLS);
                            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE_COLS));
                        }}
                        t={t}
                    />
                )}

                <div className="i18n-mgr__table-wrap">
                    <table className="i18n-mgr__table">
                        <thead>
                            <tr>
                                <th>{t('table.key')}</th>
                                {displayCols.map((code) => {
                                    const m = langMeta(code);
                                    return (
                                        <th key={code} style={code === primaryLang ? { color: 'var(--sp-primary)' } : undefined}>
                                            {m.flag} {m.shortCode || code.toUpperCase()}
                                        </th>
                                    );
                                })}
                                <th>{t('table.missingIn')}</th>
                                <th>{t('table.status')}</th>
                                <th>{t('table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.key} className={row.isManual ? 'i18n-mgr__row--manual' : undefined}>
                                    <td>
                                        <code style={{ fontSize: 12 }}>{row.key}</code>
                                        {row.isManual ? <span title={row.manualNote}> ✏️</span> : null}
                                    </td>
                                    {displayCols.map((code) => (
                                        <td key={code} className={!row.translations?.[code] ? 'i18n-mgr__cell-missing' : undefined}>
                                            <div className="i18n-mgr__cell-value">
                                                {row.translations?.[code] || '—'}
                                            </div>
                                        </td>
                                    ))}
                                    <td style={{ fontSize: 12 }}>
                                        {(row.missingIn || []).map((c) => langMeta(c).shortCode || c).join(', ') || '—'}
                                    </td>
                                    <td>
                                        {row.isManual ? t('status.manual') : row.missingIn?.length === 0 ? t('status.ok') : t('status.partial')}
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="translate-all-btn"
                                            onClick={() => handleTranslateAll(row)}
                                            disabled={translatingKey === row.key || !!busy}
                                            title={t('actions.translateAllRow')}
                                        >
                                            {translatingKey === row.key ? '⏳' : '🤖'}
                                        </button>
                                        <button
                                            type="button"
                                            className="sp-btn sp-btn--outline sp-btn--sm"
                                            style={{ marginLeft: 4 }}
                                            onClick={() => openEdit(row)}
                                        >
                                            {t('actions.edit')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={onPageChange} t={t} />
            </>
        );
    }

    return (
        <OwnerPageLayout title={t('title')} subtitle={t('subtitle')}>
            <div className="i18n-mgr__tabs">
                {tabs.map((tb) => (
                    <button
                        key={tb.id}
                        type="button"
                        className={`i18n-mgr__tab${tab === tb.id ? ' i18n-mgr__tab--active' : ''}`}
                        onClick={() => setTab(tb.id)}
                    >
                        {tb.label}
                    </button>
                ))}
            </div>

            {loading && !report && <p>{t('jobRunning')}</p>}

            {tab === 'dashboard' && report && (
                <>
                    <div className="i18n-mgr__stats">
                        <StatCard icon="🔑" label={t('stats.totalKeys')} value={report.totalKeys} t={t} />
                        <StatCard
                            icon="✅"
                            label={t('stats.fullyTranslated')}
                            value={report.fullyTranslated}
                            delta={lastDelta?.fullyTranslated}
                            t={t}
                        />
                        <StatCard
                            icon="⚠️"
                            label={t('stats.partial')}
                            value={report.partial}
                            delta={lastDelta?.partial}
                            t={t}
                        />
                        <StatCard icon="✏️" label={t('stats.manual')} value={report.manualKeys} t={t} />
                    </div>

                    <div className="i18n-mgr__panel">
                        <h3 style={{ margin: '0 0 16px' }}>{t('table.progress')}</h3>
                        <div className="i18n-mgr__table-wrap">
                            <table className="i18n-mgr__table">
                                <thead>
                                    <tr>
                                        <th>{t('table.language')}</th>
                                        <th>{t('table.translated')}</th>
                                        <th>{t('table.missing')}</th>
                                        <th>{t('table.progress')}</th>
                                        <th>{t('table.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(report.byLanguage || []).map((lang) => {
                                        const meta = langMeta(lang.code);
                                        const pct = lang.total ? Math.round(lang.translated / lang.total * 100) : 0;
                                        return (
                                            <tr key={lang.code}>
                                                <td>{meta.flag} {meta.nativeLabel}</td>
                                                <td style={{ color: '#2e7d32', fontWeight: 600 }}>{lang.translated}</td>
                                                <td style={{ color: lang.missing > 0 ? '#c62828' : '#2e7d32' }}>{lang.missing}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <div className="i18n-mgr__progress" style={{ flex: 1 }}>
                                                            <div style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    {lang.missing > 0 && (
                                                        <button
                                                            type="button"
                                                            className="sp-btn sp-btn--outline sp-btn--sm"
                                                            disabled={!!busy}
                                                            onClick={() => runAction('translate', [lang.code])}
                                                        >
                                                            {t('actions.translateMissing')} ({lang.missing})
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="i18n-mgr__panel">
                        <h3 style={{ margin: '0 0 12px' }}>{t('actions.title')}</h3>
                        <div className="i18n-mgr__actions">
                            <div className="i18n-mgr__action-row">
                                <button type="button" className="sp-btn sp-btn--primary" disabled={!!busy} onClick={() => runAction('audit')}>
                                    {busy === 'audit' ? t('jobRunning') : t('actions.audit')}
                                </button>
                                <div className="i18n-mgr__action-desc">
                                    <strong>{t('actions.audit')}</strong>
                                    {t('actions.auditDesc')}
                                    {auditResult && (
                                        <div style={{ marginTop: 8, fontSize: 13 }}>
                                            {auditResult.missingCount === 0
                                                ? t('actions.auditOk')
                                                : t('actions.auditMissing', { count: auditResult.missingCount })}
                                            {auditResult.autoAdded > 0 && (
                                                <div>{t('actions.auditAutoAdded', { count: auditResult.autoAdded })}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="i18n-mgr__action-row">
                                <button type="button" className="sp-btn sp-btn--outline" disabled={!!busy} onClick={() => runAction('translate')}>
                                    {busy === 'translate' ? t('translating') : t('actions.translateAll')}
                                </button>
                                <div className="i18n-mgr__action-desc">
                                    <strong>{t('actions.translateAll')}</strong>
                                    {t('actions.translateAllDesc')}
                                </div>
                            </div>
                            <div className="i18n-mgr__action-row">
                                <button type="button" className="sp-btn sp-btn--outline" disabled={!!busy} onClick={() => runAction('rebuild')}>
                                    {busy === 'rebuild' ? t('jobRunning') : t('actions.rebuild')}
                                </button>
                                <div className="i18n-mgr__action-desc">
                                    <strong>{t('actions.rebuild')}</strong>
                                    {t('actions.rebuildDesc')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {actionLog.length > 0 && (
                        <div className="i18n-mgr__panel">
                            <h3 style={{ margin: '0 0 12px' }}>{t('actionLog.title')}</h3>
                            <div className="i18n-mgr__log">
                                {actionLog.map((entry, i) => (
                                    <div key={i} className="i18n-mgr__log-entry">
                                        <strong>[{entry.time}] {entry.label}</strong>
                                        {'\n'}{entry.message}
                                        {entry.output ? `\n---\n${entry.output.slice(0, 800)}` : ''}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className="i18n-mgr__hint">
                💡 {t('hints.uiVsDemo')}
            </div>

            {tab === 'settings' && (
                <div className="i18n-mgr__panel i18n-mgr__settings">
                    <h3 style={{ margin: '0 0 8px' }}>{t('settings.title')}</h3>
                    <p style={{ color: 'var(--sp-gray-600)', margin: '0 0 20px', fontSize: 14 }}>{t('settings.deepSeekHint')}</p>
                    {i18nSettings?.keyDecryptFailed && (
                        <div style={{ marginBottom: 16, padding: 12, background: '#fff3e0', borderRadius: 8, fontSize: 14 }}>
                            ⚠️ {t('settings.keyDecryptFailed')}
                        </div>
                    )}
                    {i18nSettings?.hasDeepSeekApiKey && (
                        <div className="i18n-mgr__key-status">
                            ✅ {t('settings.hasKey')}
                            {i18nSettings.maskedDeepSeekApiKey && (
                                <span> · {t('settings.masked', { value: i18nSettings.maskedDeepSeekApiKey })}</span>
                            )}
                        </div>
                    )}
                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>{t('settings.deepSeekLabel')}</label>
                    <input
                        className="sp-input"
                        type="password"
                        placeholder={t('settings.placeholder')}
                        value={deepSeekInput}
                        onChange={(e) => setDeepSeekInput(e.target.value)}
                        style={{ maxWidth: 480, width: '100%' }}
                    />
                    <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                        <button type="button" className="sp-btn sp-btn--primary" onClick={saveDeepSeekKey} disabled={!deepSeekInput.trim()}>
                            {t('settings.save')}
                        </button>
                        {settingsMsg && <span style={{ color: '#2e7d32', fontSize: 14 }}>{settingsMsg}</span>}
                    </div>
                </div>
            )}

            {tab === 'demo' && (
                <div className="i18n-mgr__panel">
                    <h3 style={{ margin: '0 0 4px' }}>{t('demo.title')}</h3>
                    <p style={{ color: 'var(--sp-gray-600)', margin: '0 0 8px', fontSize: 14 }}>{t('demo.subtitle')}</p>
                    <p className="i18n-mgr__hint" style={{ marginBottom: 16, fontSize: 13 }}>{t('hintsDemo')}</p>
                    <div className="i18n-mgr__filters">
                        <select
                            className="sp-input"
                            value={demoSchoolId}
                            onChange={(e) => { setDemoSchoolId(e.target.value); setDemoPage(1); }}
                        >
                            {!demoSchools.length && <option value="">{t('demo.noSchool')}</option>}
                            {demoSchools.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        <input
                            className="sp-input"
                            placeholder={t('demo.search')}
                            value={demoSearch}
                            onChange={(e) => { setDemoSearch(e.target.value); setDemoPage(1); }}
                            style={{ flex: 1, minWidth: 180 }}
                        />
                        <button
                            type="button"
                            className="sp-btn sp-btn--outline"
                            disabled={!!demoBusy || !demoSchoolId}
                            onClick={translateDemoMissing}
                        >
                            {demoBusy === 'translate' ? t('jobRunning') : t('demo.translateMissing')}
                        </button>
                    </div>
                    <div className="i18n-mgr__subtabs" style={{ marginTop: 12 }}>
                        {[
                            { id: 'groups', label: t('demo.entityGroups') },
                            { id: 'rooms', label: t('demo.entityRooms') },
                            { id: 'events', label: t('demo.entityEvents') },
                            { id: 'students', label: t('demo.entityStudents') },
                            { id: 'staff', label: t('demo.entityStaff') },
                        ].map((et) => (
                            <button
                                key={et.id}
                                type="button"
                                className={`i18n-mgr__subtab${demoEntityType === et.id ? ' i18n-mgr__subtab--active' : ''}`}
                                onClick={() => { setDemoEntityType(et.id); setDemoPage(1); }}
                            >
                                {et.label}
                            </button>
                        ))}
                    </div>
                    <LanguageColumnBar
                        visibleCols={visibleCols}
                        primaryLang={primaryLang}
                        onToggle={toggleCol}
                        onPrimary={setPrimary}
                        onShowAll={() => {
                            const all = SUPPORTED_LANGUAGES.map((l) => l.code);
                            setVisibleCols(all);
                            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(all));
                        }}
                        onHideExtra={() => {
                            setVisibleCols(DEFAULT_VISIBLE_COLS);
                            localStorage.setItem(COLS_STORAGE_KEY, JSON.stringify(DEFAULT_VISIBLE_COLS));
                        }}
                        t={t}
                    />
                    <div className="i18n-mgr__table-wrap">
                        <table className="i18n-mgr__table">
                            <thead>
                                <tr>
                                    <th>{t('demo.rowKey')}</th>
                                    <th>{t('demo.field')}</th>
                                    <th>{t('demo.translationMode')}</th>
                                    <th>{t('demo.ruSource')}</th>
                                    {displayCols.filter((c) => c !== 'ru').map((code) => {
                                        const m = langMeta(code);
                                        return <th key={code}>{m.flag} {m.shortCode}</th>;
                                    })}
                                    <th>{t('table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {demoRows.map((row) => (
                                    <tr key={row.rowKey || `${row.entityType}-${row.entityId}-${row.field}`}>
                                        <td><code style={{ fontSize: 11 }}>{row.rowKey}</code></td>
                                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{row.fieldLabel || row.field}</td>
                                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                            {row.translationMode === 'ethnic' ? (
                                                <span title={t('demo.modeEthnicHint')}>🌍 {t('demo.modeEthnic')}</span>
                                            ) : (
                                                <span title={t('demo.modeLiteralHint')}>📝 {t('demo.modeLiteral')}</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="i18n-mgr__cell-value" style={{ maxWidth: 280 }}>
                                                <strong>{row.ruValue}</strong>
                                            </div>
                                        </td>
                                        {displayCols.filter((c) => c !== 'ru').map((code) => (
                                            <td key={code} className={!row.translations?.[code] ? 'i18n-mgr__cell-missing' : undefined}>
                                                <div className="i18n-mgr__cell-value">{row.translations?.[code] || '—'}</div>
                                            </td>
                                        ))}
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <button
                                                type="button"
                                                className="sp-btn sp-btn--outline sp-btn--sm"
                                                title={t('demo.translateRowMissing')}
                                                disabled={!!demoBusy}
                                                onClick={() => translateDemoRow(row, false)}
                                            >
                                                🤖
                                            </button>
                                            <button
                                                type="button"
                                                className="sp-btn sp-btn--outline sp-btn--sm"
                                                style={{ marginLeft: 4 }}
                                                onClick={() => setEditDemoRow({
                                                    ...row,
                                                    editTranslations: { ...row.translations, ru: row.ruValue },
                                                    editTranslationMode: row.translationMode || 'literal',
                                                })}
                                            >
                                                {t('actions.edit')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={demoPage} total={demoTotal} pageSize={PAGE_SIZE} onChange={setDemoPage} t={t} />
                </div>
            )}

            {tab === 'keys' && (
                <div className="i18n-mgr__panel">
                    <div className="i18n-mgr__filters">
                        <input
                            className="sp-input"
                            placeholder={t('filters.search')}
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                            style={{ flex: 1, minWidth: 200 }}
                        />
                        <select className="sp-input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}>
                            <option value="all">{t('filters.all')}</option>
                            <option value="ok">{t('status.ok')}</option>
                            <option value="partial">{t('status.partial')}</option>
                            <option value="manual">{t('status.manual')}</option>
                            <option value="missing">{t('status.missing')}</option>
                        </select>
                        <select className="sp-input" value={filters.lang} onChange={(e) => setFilters({ ...filters, lang: e.target.value, page: 1 })}>
                            <option value="all">{t('filters.allLanguages')}</option>
                            {SUPPORTED_LANGUAGES.map((l) => (
                                <option key={l.code} value={l.code}>{l.flag} {l.nativeLabel}</option>
                            ))}
                        </select>
                        <select className="sp-input" value={filters.namespace} onChange={(e) => setFilters({ ...filters, namespace: e.target.value, page: 1 })}>
                            <option value="all">{t('filters.allNamespaces')} ({namespaces.length})</option>
                            {namespaces.map((ns) => (
                                <option key={ns.name} value={ns.name}>{ns.name} ({ns.keyCount})</option>
                            ))}
                        </select>
                        {filters.namespace !== 'all' && (
                            <button
                                type="button"
                                className="btn-translate-ns sp-btn sp-btn--outline sp-btn--sm"
                                onClick={() => handleTranslateNamespace(filters.namespace)}
                                disabled={!!translatingNamespace || !!busy}
                                title={t('actions.translateNamespaceHint')}
                            >
                                {translatingNamespace === filters.namespace
                                    ? `⏳ ${t('actions.translatingNamespace')}`
                                    : `🤖 ${t('actions.translateNamespace')}`}
                            </button>
                        )}
                    </div>
                    {renderKeysTable(keys, keysTotal, filters.page, (p) => setFilters({ ...filters, page: p }))}
                </div>
            )}

            {tab === 'missing' && report && (
                <div className="i18n-mgr__panel">
                    <div className="i18n-mgr__subtabs">
                        <button
                            type="button"
                            className={`i18n-mgr__subtab${missingSub === 'partial' ? ' i18n-mgr__subtab--active' : ''}`}
                            onClick={() => { setMissingSub('partial'); setMissingPage(1); }}
                        >
                            {t('missing.partialTab')} ({report.partial})
                        </button>
                        <button
                            type="button"
                            className={`i18n-mgr__subtab${missingSub === 'code' ? ' i18n-mgr__subtab--active' : ''}`}
                            onClick={() => { setMissingSub('code'); setMissingPage(1); }}
                        >
                            {t('missing.codeTab')} ({codeMissingTotal})
                        </button>
                    </div>

                    {missingSub === 'partial' && (
                        missingTotal === 0
                            ? <p style={{ color: '#2e7d32' }}>✅ {t('allGood')}</p>
                            : renderKeysTable(missingItems, missingTotal, missingPage, setMissingPage, false)
                    )}

                    {missingSub === 'code' && (
                        codeMissingTotal === 0
                            ? <p style={{ color: '#2e7d32' }}>✅ {t('missing.allGood')}</p>
                            : (
                                <>
                                    <div className="i18n-mgr__table-wrap">
                                        <table className="i18n-mgr__table">
                                            <thead>
                                                <tr>
                                                    <th>{t('table.key')}</th>
                                                    <th>{t('missing.foundIn')}</th>
                                                    <th>{t('table.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {codeMissingPage.map((item) => (
                                                    <tr key={item.key}>
                                                        <td><code>{item.key}</code></td>
                                                        <td style={{ fontSize: 12 }}>{(item.files || []).join(', ')}</td>
                                                        <td>
                                                            <button type="button" className="sp-btn sp-btn--outline sp-btn--sm" onClick={() => openEdit({ key: item.key.replace(':', '.'), translations: {} })}>
                                                                {t('actions.edit')}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Pagination page={missingPage} total={codeMissingTotal} pageSize={PAGE_SIZE} onChange={setMissingPage} t={t} />
                                </>
                            )
                    )}
                </div>
            )}

            {tab === 'history' && (
                <div className="i18n-mgr__panel">
                    <div className="i18n-mgr__table-wrap">
                        <table className="i18n-mgr__table">
                            <thead>
                                <tr>
                                    <th>{t('history.date')}</th>
                                    <th>{t('history.key')}</th>
                                    <th>{t('history.lang')}</th>
                                    <th>{t('history.oldValue')}</th>
                                    <th>{t('history.newValue')}</th>
                                    <th>{t('history.changedBy')}</th>
                                    <th>{t('history.type')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((row) => (
                                    <tr key={row.id} style={row.key === '[system]' ? { background: '#f0f4ff' } : undefined}>
                                        <td style={{ whiteSpace: 'nowrap' }}>{new Date(row.date).toLocaleString()}</td>
                                        <td><code>{row.key}</code></td>
                                        <td>{row.lang}</td>
                                        <td className="i18n-mgr__cell-value">{row.oldValue || '—'}</td>
                                        <td className="i18n-mgr__cell-value">{row.newValue || '—'}</td>
                                        <td>{row.changedBy || '—'}</td>
                                        <td>{row.isAutomatic ? `🤖 ${t('history.automatic')}` : `✏️ ${t('history.manual')}`}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={historyPage} total={historyTotal} pageSize={PAGE_SIZE} onChange={setHistoryPage} t={t} />
                </div>
            )}

            {editKey && (
                <EditKeyModal
                    keyData={editKey}
                    onClose={() => setEditKey(null)}
                    onSave={handleKeySaved}
                    t={t}
                    ensureDeepSeekKey={ensureDeepSeekKey}
                />
            )}

            {editDemoRow && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                }}>
                    <div className="sp-data-card" style={{ maxWidth: 640, width: '95%', maxHeight: '90vh', overflow: 'auto', padding: 28 }}>
                        <h2 style={{ margin: '0 0 8px' }}>{t('demo.title')}</h2>
                        <code style={{ fontSize: 12, color: 'var(--sp-gray-600)' }}>{editDemoRow.rowKey}</code>
                        <p style={{ margin: '8px 0 16px', color: 'var(--sp-gray-600)' }}>
                            <strong>{editDemoRow.fieldLabel || editDemoRow.field}</strong>
                            <br />
                            {editDemoRow.ruValue}
                        </p>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                            {t('demo.translationMode')}
                        </label>
                        <select
                            className="sp-input"
                            style={{ width: '100%', marginBottom: 8 }}
                            value={editDemoRow.editTranslationMode || editDemoRow.translationMode || 'literal'}
                            onChange={(e) => setEditDemoRow({
                                ...editDemoRow,
                                editTranslationMode: e.target.value,
                            })}
                        >
                            <option value="literal">{t('demo.modeLiteral')}</option>
                            <option value="ethnic">{t('demo.modeEthnic')}</option>
                        </select>
                        <p style={{ fontSize: 12, color: 'var(--sp-gray-600)', margin: '0 0 16px' }}>
                            {(editDemoRow.editTranslationMode || editDemoRow.translationMode) === 'ethnic'
                                ? t('demo.modeEthnicHint')
                                : t('demo.modeLiteralHint')}
                        </p>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                            <button
                                type="button"
                                className="sp-btn sp-btn--outline sp-btn--sm"
                                disabled={!!demoBusy}
                                onClick={() => translateDemoRow(editDemoRow, false)}
                            >
                                🤖 {t('demo.translateRowMissing')}
                            </button>
                            <button
                                type="button"
                                className="sp-btn sp-btn--outline sp-btn--sm"
                                disabled={!!demoBusy}
                                onClick={() => {
                                    if (window.confirm(t('demo.translateRowAllHint'))) {
                                        translateDemoRow(editDemoRow, true);
                                    }
                                }}
                            >
                                🤖 {t('demo.translateRowAll')}
                            </button>
                        </div>
                        {SUPPORTED_LANGUAGES.filter((l) => l.code !== 'ru').map((lang) => (
                            <div key={lang.code} style={{ marginBottom: 12 }}>
                                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    {lang.flag} {lang.nativeLabel}
                                </label>
                                <input
                                    className="sp-input"
                                    style={{ width: '100%' }}
                                    value={editDemoRow.editTranslations?.[lang.code] || ''}
                                    onChange={(e) => setEditDemoRow({
                                        ...editDemoRow,
                                        editTranslations: {
                                            ...editDemoRow.editTranslations,
                                            [lang.code]: e.target.value,
                                        },
                                    })}
                                />
                            </div>
                        ))}
                        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
                            <button type="button" className="sp-btn sp-btn--outline" onClick={() => setEditDemoRow(null)}>
                                {t('actions.cancel')}
                            </button>
                            <button
                                type="button"
                                className="sp-btn sp-btn--primary"
                                onClick={async () => {
                                    const mode = editDemoRow.editTranslationMode || editDemoRow.translationMode || 'literal';
                                    if (mode !== editDemoRow.translationMode) {
                                        await saveDemoRowMode(editDemoRow.rowKey, mode);
                                    }
                                    await saveDemoRow(editDemoRow, editDemoRow.editTranslations);
                                }}
                            >
                                {t('demo.saveRow')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </OwnerPageLayout>
    );
}
